import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { ImageOff, Loader2 } from "lucide-react";

// Loads an auth-protected photo through the BACKEND PROXY (/files/{id}[/thumb]) as a blob.
// The proxy serves disk-cache-first then B2 (B2 stays the only source of truth), and stamps
// immutable 1-year Cache-Control so the browser reuses the bytes on scroll / re-open / next
// visit — a photo hits B2 essentially once, ever. Two speed features remain: LAZY (only fetch
// once scrolled near the viewport) and THUMB (small /thumb variant in grids).
//
// Resilience against a B2 throttle/outage (SlowDown 503) or flaky warehouse wifi:
//   - BOUNDED retry (a few attempts then stop — never an infinite retry that burns tablet
//     battery / network),
//   - EXPONENTIAL backoff with JITTER so a wall of simultaneously-failed images does not
//     retry in lockstep and make a throttle worse,
//   - then a HONEST placeholder (broken-photo icon) that is visibly different from "this part
//     has no photo" (in that case the parent renders no AuthImg at all).
//
// `reserve` — THE FIX FOR "Wrong photo?" ON A BLACK SCREEN. The loading placeholder used to be a
// div carrying only the image's `max-w-/max-h-` classes, so inside a lightbox (where there is no
// width or height to inherit, only a ceiling) it collapsed to ZERO and the viewer showed nothing
// but the action buttons. Callers that render into empty space pass a reserved size.
//
// `onState` — "loading" | "loaded" | "failed". A caller that must not offer an action over a photo
// nobody can see yet (the lightbox) needs to know when the bytes are actually up.
const MAX_ATTEMPTS = 3;         // initial try + 2 retries
const BASE_DELAY_MS = 500;      // 0.5s, ~1s, ~2s (plus jitter)

function backoffDelay(attempt) {
  const expo = BASE_DELAY_MS * Math.pow(2, attempt); // attempt 0,1,2 -> 500,1000,2000
  const jitter = Math.random() * BASE_DELAY_MS;      // spread the herd
  return Math.min(expo + jitter, 6000);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// A file the server says is GONE (404/410) is not retried and never spins (2 Nov): it shows "Photo file missing"
// straight away. `missing` (the photo record's bytes_missing flag) skips the request altogether.
const GONE = new Set([404, 410]);

export function AuthImg({ fileId, className, alt = "photo", thumb = false, style, draggable,
                          reserve, onState, missing = false, big = false, "data-testid": testid }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(missing ? "gone" : false);
  const [inView, setInView] = useState(false);
  const holderRef = useRef(null);
  // Kept in a ref so an inline arrow from the parent cannot re-fire the fetch effect.
  const stateCb = useRef(onState);
  stateCb.current = onState;

  useEffect(() => {
    const el = holderRef.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    if (missing) { setErr("gone"); stateCb.current?.("failed"); return; }
    if (!inView) return;
    let objUrl; let active = true;
    setErr(false); setSrc(null);
    stateCb.current?.("loading");
    const path = thumb ? `/files/${fileId}/thumb` : `/files/${fileId}`;
    (async () => {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
          const res = await api.get(path, { responseType: "blob" });
          if (!active) return;
          objUrl = URL.createObjectURL(res.data);
          setSrc(objUrl);
          stateCb.current?.("loaded");
          return;                                   // success — done
        } catch (e) {
          if (!active) return;
          if (GONE.has(e?.response?.status)) { setErr("gone"); stateCb.current?.("failed"); return; }
          if (attempt < MAX_ATTEMPTS - 1) {
            await sleep(backoffDelay(attempt));      // wait, then retry (bounded + jittered)
            if (!active) return;
          }
        }
      }
      if (active) { setErr(true); stateCb.current?.("failed"); }   // honest failure placeholder
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [inView, fileId, thumb, missing]);

  const held = reserve ? { minWidth: reserve, minHeight: reserve, ...style } : style;

  if (err === "gone") return (
    <div ref={holderRef} className={`${className} flex flex-col items-center justify-center gap-1 bg-slate-100 text-slate-500 ${big ? "min-w-[220px] min-h-[160px] rounded-xl p-6 gap-2" : ""}`}
         style={held} title="This photo's file is missing" data-testid="authimg-missing">
      <ImageOff className={big ? "w-10 h-10" : "w-5 h-5"} />
      <span className={`${big ? "text-base" : "text-[10px]"} font-semibold leading-tight text-center px-1`}>Photo file missing</span>
    </div>
  );
  if (err) return (
    <div ref={holderRef} className={`${className} flex items-center justify-center bg-slate-100`}
         style={held} title="Photo failed to load" data-testid="authimg-load-failed">
      <ImageOff className="w-5 h-5 text-slate-400" />
    </div>
  );
  if (!src) return (
    <div ref={holderRef} className={`${className} flex items-center justify-center bg-slate-100`}
         style={held} data-testid="authimg-loading">
      <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
    </div>
  );
  return <img ref={holderRef} src={src} alt={alt} className={className} style={style} draggable={draggable} loading="lazy" data-testid={testid} />;
}
